import clsx from 'clsx';
import type { CSSProperties } from 'react';

import { BaseContainer, type BaseContainerProps } from '@commons/containers/BaseContainer';
import { getResponsiveClasses } from '@commons/helpers/styles';
import type { Alignment, ResponsiveStyle, Spacing } from '@commons/types/styles';

import styles from './index.module.scss';

/**
 * The amount of supported grid rows and columns.
 */

export type GridSize = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Supported grid justify values.
 */

export type GridJustify = Alignment | 'space-between' | 'space-around';

/**
 * Supported grid alignment values.
 */

export type GridAlignment = Alignment | 'stretch';

/**
 * Grid container component props.
 *
 * Based on the BaseContainer props.
 */

export type GridProps = BaseContainerProps & {
  /**
   * The gap between the grid items.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <Grid gap="md" />
   * <Grid gap={20} />
   * <Grid gap={{ xs: 'sm', lg: 'md' }} />
   */
  gap?: ResponsiveStyle<Spacing> | number;

  /**
   * The row gap between the grid items.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <Grid rowGap="md" />
   * <Grid rowGap={20} />
   * <Grid rowGap={{ xs: 'sm', lg: 'md' }} />
   */
  rowGap?: ResponsiveStyle<Spacing> | number;

  /**
   * The column gap between the grid items.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <Grid columnGap="md" />
   * <Grid columnGap={20} />
   * <Grid columnGap={{ xs: 'sm', lg: 'md' }} />
   */
  columnGap?: ResponsiveStyle<Spacing> | number;

  /**
   * The `align-items` property of the grid container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Grid align="center" />
   * <Grid align={{ xs: 'center', lg: 'start' }} />
   */
  align?: ResponsiveStyle<GridAlignment>;

  /**
   * The `justify-items` property of the grid container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Grid justify="center" />
   * <Grid justify={{ xs: 'center', lg: 'start' }} />
   */
  justify?: ResponsiveStyle<GridJustify>;

  /**
   * The amount of rows in the grid.
   *
   * Can be a number or a responsive object with breakpoints as keys.
   *
   * @example
   * <Grid rows={3} />
   * <Grid rows={{ xs: 3, lg: 6 }} />
   */
  rows?: ResponsiveStyle<GridSize>;

  /**
   * The amount of columns in the grid.
   *
   * Can be a number or a responsive object with breakpoints as keys.
   *
   * @example
   * <Grid columns={3} />
   * <Grid columns={{ xs: 3, lg: 6 }} />
   */
  columns?: ResponsiveStyle<GridSize>;
};

/**
 * Grid container component.
 */

export const Grid = (props: GridProps) => {
  const {
    gap,
    rowGap,
    columnGap,
    align,
    justify,
    rows,
    columns,
    children,
    className,
    style,
    ref,
    ...rest
  } = props;

  // Preparing the responsive classes.
  // Used when a prop needs to change depending on the breakpoint.
  // i.e. columns = { xs: 1, lg: 3 }
  const responsiveClasses = [
    ...getResponsiveClasses('align', align),
    ...getResponsiveClasses('justify', justify),
  ];

  const classes = clsx(
    'grid',
    styles.grid,
    getResponsiveClasses('rows', rows),
    getResponsiveClasses('columns', columns),
    gap && typeof gap !== 'number' && getResponsiveClasses('gap', gap),
    rowGap && typeof rowGap !== 'number' && getResponsiveClasses('rowGap', rowGap),
    columnGap && typeof columnGap !== 'number' && getResponsiveClasses('columnGap', columnGap),
    responsiveClasses.map((className) => styles[className]),
    className
  );

  const inlineStyles: CSSProperties = {
    gap: gap && typeof gap === 'number' ? `${gap}px` : undefined,
    rowGap: rowGap && typeof rowGap === 'number' ? `${rowGap}px` : undefined,
    columnGap: columnGap && typeof columnGap === 'number' ? `${columnGap}px` : undefined,
    ...style,
  };

  return (
    <BaseContainer ref={ref} className={classes} style={inlineStyles} {...rest}>
      {children}
    </BaseContainer>
  );
};

/**
 * Grid item component props.
 */

export type GridItemProps = BaseContainerProps & {
  align?: ResponsiveStyle<GridAlignment>;
  justify?: ResponsiveStyle<GridJustify>;
};

/**
 * Grid item component.
 *
 * Can be used to easily set `align`, and `justify` properties without CSS.
 */

export const GridItem = (props: GridItemProps) => {
  const { align, justify, children, className, ref, ...rest } = props;

  const responsiveClasses = [
    ...getResponsiveClasses('align', align),
    ...getResponsiveClasses('justify', justify),
  ];

  const classes = clsx(
    styles.gridItem,
    responsiveClasses.map((className) => styles[className]),
    className
  );

  return (
    <BaseContainer ref={ref} className={classes} {...rest}>
      {children}
    </BaseContainer>
  );
};

// Assigning GridItem to the Grid namespace.

Grid.Item = GridItem;

export default Grid;
